import { Button } from "@designs-by-maryam/ui/components/button";
import { cn } from "@designs-by-maryam/ui/lib/utils";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import {
	ArrowRight,
	Check,
	Hammer,
	Home,
	type LucideIcon,
	MessageCircle,
	Palette,
	Pencil,
	Ruler,
	Sparkles,
} from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useMemo, useState } from "react";
import {
	SERVICES_SECTION,
	SERVICES as STATIC_SERVICES,
} from "@/data/services-content";
import { servicesQueryOptions } from "@/lib/query-options";

/**
 * Icon name to Lucide component mapping
 * Used to convert CMS icon_name strings to actual components
 */
const ICON_MAP: Record<string, LucideIcon> = {
	Home,
	Hammer,
	MessageCircle,
	Palette,
	Pencil,
	Ruler,
	Sparkles,
};

/**
 * Props for ServicesSection component
 * Supports both direct CMS fetch and Puck-resolved data
 */
type ServicesSectionProps = {
	className?: string;
	/** Pre-resolved services from Puck's resolveData */
	resolvedServices?: Array<{
		id: string;
		title: string;
		shortTitle: string;
		description: string;
		iconName: string;
		features: string[];
		ctaText: string;
		ctaLink: string;
	}>;
};

export function ServicesSection({
	className,
	resolvedServices,
}: ServicesSectionProps) {
	const [activeTab, setActiveTab] = useState(0);

	// Fetch services from CMS - prefetched in route loader
	// Skip if we have Puck-resolved data
	const { data: cmsServices } = useQuery({
		...servicesQueryOptions({ featured: true }),
		staleTime: 30 * 60 * 1000, // 30 minutes
		enabled: !resolvedServices || resolvedServices.length === 0,
	});

	// Transform CMS services to component format with static fallback
	const services = useMemo(() => {
		// First priority: Puck-resolved services
		if (resolvedServices && resolvedServices.length > 0) {
			return resolvedServices.map((svc) => ({
				id: svc.id,
				title: svc.title,
				shortTitle: svc.shortTitle,
				description: svc.description,
				features: svc.features,
				ctaText: svc.ctaText,
				ctaLink: svc.ctaLink,
				projectType: "residential" as const,
				icon: ICON_MAP[svc.iconName] ?? Home,
				images: [] as string[],
			}));
		}
		// Second priority: CMS services from useQuery
		if (cmsServices?.data && cmsServices.data.length > 0) {
			return cmsServices.data.map((svc) => ({
				id: svc.id,
				title: svc.title,
				shortTitle: svc.short_title,
				description: svc.description,
				features: svc.features as string[],
				ctaText: svc.cta_text,
				ctaLink: svc.cta_link,
				projectType: svc.project_type ?? "residential",
				icon: ICON_MAP[svc.icon_name] ?? Home,
				images: (svc.images as string[]) ?? [],
			}));
		}
		// Fall back to static services data
		return STATIC_SERVICES;
	}, [cmsServices, resolvedServices]);

	const activeService = services[activeTab] ?? services[0];

	// Safety: Reset tab if out of bounds (can happen if CMS has fewer services)
	if (!services[activeTab] && activeTab !== 0) {
		setActiveTab(0);
	}

	// Don't render if no services available
	if (!activeService) {
		return null;
	}

	return (
		<section className={cn("py-10 md:py-14", className)}>
			<div className="container mx-auto px-4 md:px-6">
				{/* Section Header */}
				<div className="mb-12 text-center">
					<h2 className="font-bold text-3xl tracking-tight md:text-4xl lg:text-5xl">
						{SERVICES_SECTION.title}
					</h2>
					<p className="mx-auto mt-4 max-w-2xl text-muted-foreground md:text-lg">
						{SERVICES_SECTION.description}
					</p>
				</div>

				{/* Tab Navigation */}
				<div className="mb-8 flex justify-center">
					<div
						className="inline-flex rounded-xl border bg-muted/30 p-1 shadow-sm backdrop-blur-sm"
						role="tablist"
					>
						{services.map((service, index) => {
							const Icon = service.icon;
							return (
								<button
									aria-controls={`service-panel-${service.id}`}
									aria-selected={activeTab === index}
									className={cn(
										"relative flex items-center gap-2 rounded-lg px-4 py-3 font-medium text-sm transition-all md:px-6",
										activeTab === index
											? "text-foreground"
											: "text-muted-foreground hover:text-foreground",
									)}
									id={`service-tab-${service.id}`}
									key={service.id}
									onClick={() => setActiveTab(index)}
									role="tab"
									type="button"
								>
									{activeTab === index && (
										<motion.div
											className="absolute inset-0 rounded-lg bg-background shadow-md ring-2 ring-ring/20"
											layoutId="activeTab"
											transition={{
												type: "spring",
												bounce: 0.2,
												duration: 0.6,
											}}
										/>
									)}
									<span className="relative z-10 flex items-center gap-2">
										<Icon
											className={cn(
												"h-4 w-4 transition-all",
												activeTab === index && "text-primary",
											)}
										/>
										<span className="hidden sm:inline">
											{service.shortTitle}
										</span>
									</span>
								</button>
							);
						})}
					</div>
				</div>

				{/* Bento Grid Content */}
				<AnimatePresence mode="wait">
					<motion.div
						animate={{ opacity: 1, y: 0 }}
						aria-labelledby={`service-tab-${activeService.id}`}
						className="grid gap-4 lg:grid-cols-5 lg:grid-rows-2"
						exit={{ opacity: 0, y: 10 }}
						id={`service-panel-${activeService.id}`}
						initial={{ opacity: 0, y: 10 }}
						key={activeService.id}
						role="tabpanel"
						transition={{ duration: 0.4, ease: "easeOut" }}
					>
						{/* Main Content Card - spans 3 cols, 2 rows */}
						<div className="group relative overflow-hidden rounded-2xl border bg-card p-6 shadow-lg transition-shadow duration-300 hover:shadow-2xl hover:shadow-primary/10 lg:col-span-3 lg:row-span-2">
							{/* Subtle decorative background */}
							<div className="-translate-y-20 absolute top-0 right-0 h-48 w-48 translate-x-20 rounded-full bg-primary/10 blur-xl transition-opacity duration-500 group-hover:opacity-100 dark:bg-primary/5" />

							<div className="relative flex h-full flex-col">
								{/* Icon */}
								<motion.div
									animate={{ scale: 1, rotate: 0 }}
									className="mb-6 flex h-16 w-16 items-center justify-center rounded-xl bg-primary/10 text-primary shadow-md transition-all duration-300 group-hover:scale-110 group-hover:shadow-lg dark:bg-primary/20 dark:text-primary"
									initial={{ scale: 0.8, rotate: -10 }}
									transition={{ duration: 0.5, ease: "easeOut" }}
								>
									<activeService.icon className="h-8 w-8" />
								</motion.div>

								{/* Title & Description */}
								<motion.h3
									animate={{ opacity: 1, x: 0 }}
									className="mb-3 font-semibold text-2xl tracking-tight transition-colors group-hover:text-primary md:text-3xl"
									initial={{ opacity: 0, x: -20 }}
									transition={{ duration: 0.4, delay: 0.1 }}
								>
									{activeService.title}
								</motion.h3>
								<motion.p
									animate={{ opacity: 1, x: 0 }}
									className="mb-6 text-muted-foreground leading-relaxed"
									initial={{ opacity: 0, x: -20 }}
									transition={{ duration: 0.4, delay: 0.2 }}
								>
									{activeService.description}
								</motion.p>

								{/* Features */}
								<ul className="mb-8 grid gap-3 sm:grid-cols-2">
									{activeService.features.map((feature, index) => (
										<motion.li
											animate={{ opacity: 1, x: 0 }}
											className="flex items-start gap-2.5 text-sm"
											initial={{ opacity: 0, x: -20 }}
											key={feature}
											transition={{ duration: 0.3, delay: 0.3 + index * 0.05 }}
										>
											<div className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/10 dark:bg-primary/20">
												<Check className="h-3.5 w-3.5 text-primary" />
											</div>
											<span className="text-muted-foreground">{feature}</span>
										</motion.li>
									))}
								</ul>

								{/* CTA */}
								<motion.div
									animate={{ opacity: 1, y: 0 }}
									initial={{ opacity: 0, y: 20 }}
									transition={{ duration: 0.4, delay: 0.5 }}
								>
									<Button
										asChild
										className="group/btn transition-all duration-300 hover:shadow-lg hover:shadow-primary/20"
										size="lg"
										variant="brand"
									>
										<Link to={activeService.ctaLink}>
											{activeService.ctaText}
											<ArrowRight className="ml-2 h-4 w-4 transition-transform duration-300 group-hover/btn:translate-x-1" />
										</Link>
									</Button>
								</motion.div>
							</div>
						</div>

						{/* Image Grid - Right side */}
						<motion.div
							animate={{ opacity: 1, scale: 1 }}
							className="group/img relative overflow-hidden rounded-2xl shadow-lg transition-shadow duration-300 hover:shadow-2xl lg:col-span-2 lg:row-span-1"
							initial={{ opacity: 0, scale: 0.95 }}
							transition={{ duration: 0.5, delay: 0.2 }}
						>
							<motion.img
								alt={`${activeService.title} project example`}
								animate={{ scale: 1 }}
								className="h-full min-h-[200px] w-full object-cover transition-transform duration-700 group-hover/img:scale-110"
								height={400}
								initial={{ scale: 1.1 }}
								src={activeService.images[0]}
								transition={{ duration: 0.5 }}
								width={600}
							/>
							<div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent transition-opacity duration-300 group-hover/img:from-black/50" />
							<div className="absolute bottom-4 left-4">
								<motion.span
									animate={{ opacity: 1, y: 0 }}
									className="rounded-full bg-white/95 px-4 py-1.5 font-medium text-gray-900 text-xs capitalize shadow-lg backdrop-blur-sm"
									initial={{ opacity: 0, y: 10 }}
									transition={{ duration: 0.4, delay: 0.6 }}
								>
									{activeService.projectType}
								</motion.span>
							</div>
						</motion.div>

						{/* Secondary Images */}
						<div className="grid grid-cols-2 gap-4 lg:col-span-2 lg:row-span-1">
							<motion.div
								animate={{ opacity: 1, scale: 1 }}
								className="group/img relative overflow-hidden rounded-xl shadow-md transition-shadow duration-300 hover:shadow-xl"
								initial={{ opacity: 0, scale: 0.95 }}
								transition={{ duration: 0.5, delay: 0.3 }}
							>
								<motion.img
									alt={`${activeService.title} example 2`}
									animate={{ scale: 1 }}
									className="h-full min-h-[120px] w-full object-cover transition-transform duration-700 group-hover/img:scale-110"
									height={200}
									initial={{ scale: 1.1 }}
									src={activeService.images[1]}
									transition={{ duration: 0.5, delay: 0.1 }}
									width={300}
								/>
								<div className="absolute inset-0 bg-black/10 transition-opacity duration-300 group-hover/img:bg-black/5" />
							</motion.div>
							<motion.div
								animate={{ opacity: 1, scale: 1 }}
								className="group/img relative overflow-hidden rounded-xl shadow-md transition-shadow duration-300 hover:shadow-xl"
								initial={{ opacity: 0, scale: 0.95 }}
								transition={{ duration: 0.5, delay: 0.4 }}
							>
								<motion.img
									alt={`${activeService.title} example 3`}
									animate={{ scale: 1 }}
									className="h-full min-h-[120px] w-full object-cover transition-transform duration-700 group-hover/img:scale-110"
									height={200}
									initial={{ scale: 1.1 }}
									src={activeService.images[2]}
									transition={{ duration: 0.5, delay: 0.2 }}
									width={300}
								/>
								<div className="absolute inset-0 bg-black/10 transition-opacity duration-300 group-hover/img:bg-black/5" />
							</motion.div>
						</div>
					</motion.div>
				</AnimatePresence>
			</div>
		</section>
	);
}
